/**
 * The Minga GitHub App: observer views and webhooks (decision 0069), plus task repository access (decision 0083).
 * S1 observer tokens remain read-only; execution tokens use the same App's repository write permissions. Shared
 * definitions include HTTP views, Worker → Workspace DO payloads, trimmed webhook schemas and permission/event lists.
 *
 * Webhook schemas keep only the fields Minga reads. Decoding drops unknown keys, so GitHub adding fields never breaks
 * a delivery; a payload that does not decode is answered 204 and recorded nowhere.
 */
import { Schema } from "effect"
import { GithubLogin, GithubRepo, GitSha } from "./github"
import { RepoView } from "./http"
import { Millis, NonNegInt, PosInt, RepoId, UserId } from "./ids"

// #region ids
/** GitHub's installation id (an integer below 2^53). */
export const GithubInstallationId = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1))
export type GithubInstallationId = typeof GithubInstallationId.Type
/** GitHub's repository id: stable across renames and transfers. */
export const GithubRepoId = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1))
export type GithubRepoId = typeof GithubRepoId.Type
export const GithubAccountType = Schema.Literals(["Organization", "User"])
export type GithubAccountType = typeof GithubAccountType.Type
export const GithubInstallationStatus = Schema.Literals(["active", "suspended", "deleted"])
export type GithubInstallationStatus = typeof GithubInstallationStatus.Type
export const GithubRepositorySelection = Schema.Literals(["all", "selected"])
export type GithubRepositorySelection = typeof GithubRepositorySelection.Type
// #endregion ids

// #region constants
/** The S1 permissions: read-only, no Contents (the cloud cannot read code). The registration script keeps a copy. */
export const GITHUB_APP_PERMISSIONS_S1 = {
  metadata: "read",
  pull_requests: "read",
  checks: "read",
  statuses: "read",
  issues: "read",
} as const

/** Task execution through the same App; observer tokens retain the S1 read permissions. */
export const GITHUB_APP_EXECUTION_PERMISSIONS = {
  ...GITHUB_APP_PERMISSIONS_S1,
  contents: "write",
  pull_requests: "write",
  issues: "write",
  workflows: "write",
  actions: "write",
} as const
export const GITHUB_APP_PERMISSIONS = GITHUB_APP_EXECUTION_PERMISSIONS

/**
 * 0121 addendum (lead turns): how an agent checks push or merge access correctly. A worker stopped on a false negative:
 * `repo.permissions` describes a user's collaborator rights, so an App installation token always reads all-false.
 * Shared by the agent presets, the work-an-item skill and the turn file's `## Project setup`.
 */
export const GITHUB_ACCESS_CHECK_HINT =
  "Checking GitHub access: with the Platform Runner's GitHub App token (git author `minga-run[bot]`), `gh api repos/OWNER/REPO --jq .permissions` is always all-false (it describes a user's collaborator rights), so never conclude from it that you cannot push or merge. `gh api /installation/repositories` lists the repos the token covers. Or just attempt the action with a guard (`gh pr merge N --match-head-commit SHA`) and report GitHub's exact error if it refuses."

/** The S1 webhook events (`installation` and `installation_repositories` reach every app without being listed). */
export const GITHUB_APP_EVENTS_S1 = [
  "pull_request",
  "pull_request_review",
  "pull_request_review_comment",
  "pull_request_review_thread",
  "issue_comment",
  "check_suite",
  "check_run",
  "status",
  "repository",
] as const

/** The REST API version every GitHub call sends (`X-GitHub-Api-Version`), read on docs.github.com 2026-10-12. */
export const GITHUB_API_VERSION = "2026-03-10"
// #endregion constants

// #region views
/** Settings › GitHub: one installation linked to this workspace. */
export const GithubInstallationView = Schema.Struct({
  id: GithubInstallationId,
  accountLogin: GithubLogin,
  accountType: GithubAccountType,
  repositorySelection: GithubRepositorySelection,
  status: GithubInstallationStatus,
  linkedBy: Schema.NullOr(UserId),
  linkedByName: Schema.NullOr(Schema.String),
  linkedAt: Schema.NullOr(Millis),
  /** Active, linked, and heard from lately (core `installationHealthy`). */
  healthy: Schema.Boolean,
  lastDeliveryAt: Schema.NullOr(Millis),
  lastReconcileAt: Schema.NullOr(Millis),
  /** The app asks for more than the installation accepted. */
  permissionsPending: Schema.Boolean,
  /**
   * Why an active, linked installation is not healthy, when Minga knows: GitHub refused the app's token
   * (`token_refused`), or GitHub's deliveries are answered 401 (`webhook_signature`: the webhook secret differs).
   */
  problem: Schema.NullOr(Schema.Literals(["token_refused", "webhook_signature"])),
  settingsUrl: Schema.String,
})
export type GithubInstallationView = typeof GithubInstallationView.Type

/** `GET /api/github/config`. `slug`/`installUrl` only when configured. */
export const GithubConfigView = Schema.Struct({
  configured: Schema.Boolean,
  slug: Schema.optionalKey(Schema.String),
  installUrl: Schema.optionalKey(Schema.String),
})
export type GithubConfigView = typeof GithubConfigView.Type

/** `GET /api/me/github`: the viewer's own link. */
export const GithubMeView = Schema.Struct({
  linked: Schema.Boolean,
  login: Schema.NullOr(GithubLogin),
  githubUserId: Schema.NullOr(Schema.Number),
})
export type GithubMeView = typeof GithubMeView.Type

/** An installation the viewer's own token can see, offered for linking. */
export const GithubLinkableInstallation = Schema.Struct({
  id: GithubInstallationId,
  accountLogin: GithubLogin,
  accountType: GithubAccountType,
  linkedElsewhere: Schema.Boolean,
  /**
   * The viewer may link it (and take over a link held elsewhere): it is their own account, or GitHub's signed
   * `installation.created` names them as who installed or requested it. Seeing it is not enough (§2).
   */
  canLink: Schema.Boolean,
})
export type GithubLinkableInstallation = typeof GithubLinkableInstallation.Type

/** A registry repo that came through (or was attached to) an installation. */
export const GithubWorkspaceRepo = Schema.Struct({
  repoId: RepoId,
  githubRepoId: GithubRepoId,
  installationId: GithubInstallationId,
  /** Its PRs are watched by the app, not by runners. */
  covered: Schema.Boolean,
  /** The installation no longer includes this repo. */
  lostAccess: Schema.Boolean,
  addedVia: Schema.Literals(["url", "app"]),
})
export type GithubWorkspaceRepo = typeof GithubWorkspaceRepo.Type

/** `GET /api/workspaces/:ws/github`. `linkable` comes from the viewer's own token; empty without a link. */
export const GithubWorkspaceView = Schema.Struct({
  installations: Schema.Array(GithubInstallationView),
  linkable: Schema.Array(GithubLinkableInstallation),
  repos: Schema.Array(GithubWorkspaceRepo),
  /** Members' linked GitHub logins (merged-by lines name the member). */
  members: Schema.Array(Schema.Struct({ userId: UserId, login: GithubLogin })),
})
export type GithubWorkspaceView = typeof GithubWorkspaceView.Type

/** One row of the repo picker. `repoId` is set when the repo is already in the registry. */
export const GithubPickerRepo = Schema.Struct({
  githubRepoId: GithubRepoId,
  fullName: GithubRepo,
  private: Schema.Boolean,
  defaultBranch: Schema.String,
  repoId: Schema.NullOr(RepoId),
})
export type GithubPickerRepo = typeof GithubPickerRepo.Type
export const GithubPickerPage = Schema.Struct({
  repos: Schema.Array(GithubPickerRepo),
  hasMore: Schema.Boolean,
})
export type GithubPickerPage = typeof GithubPickerPage.Type

/** `POST /api/workspaces/:ws/github/repos`. */
export const AddGithubReposBody = Schema.Struct({
  installationId: GithubInstallationId,
  githubRepoIds: Schema.Array(GithubRepoId).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
})
export type AddGithubReposBody = typeof AddGithubReposBody.Type
export const AddGithubReposRefusal = Schema.Literals(["not_visible", "not_in_installation", "invalid"])
export const AddGithubReposResult = Schema.Struct({
  repos: Schema.Array(RepoView),
  refused: Schema.Array(Schema.Struct({ githubRepoId: GithubRepoId, reason: AddGithubReposRefusal })),
})
export type AddGithubReposResult = typeof AddGithubReposResult.Type
// #endregion views

// #region internal
/**
 * Worker → Workspace DO: which repos the app covers, which installations are healthy, and members' GitHub logins.
 * `rev` only increases (`Date.now()` at the Worker); the DO ignores a lower one.
 */
export const GithubCoverage = Schema.Struct({
  rev: NonNegInt,
  repos: Schema.Array(
    Schema.Struct({
      repoId: RepoId,
      githubRepo: GithubRepo,
      /** GitHub's id of the registry repo: installation tokens are minted for exactly these ids. */
      githubRepoId: GithubRepoId,
      installationId: GithubInstallationId,
    }),
  ),
  healthy: Schema.Array(GithubInstallationId),
  logins: Schema.Array(Schema.Struct({ userId: UserId, login: GithubLogin })),
})
export type GithubCoverage = typeof GithubCoverage.Type

/**
 * Worker → Workspace DO: a webhook's doorbell. Never a snapshot: the DO re-reads the PRs with the watch query.
 * `numbers` names the PRs; when GitHub gave none (`status`, a fork's `check_*`), `headSha` resolves them against the
 * hub's last known head SHAs.
 */
export const GithubPrsDirty = Schema.Struct({
  installationId: GithubInstallationId,
  repo: GithubRepo,
  githubRepoId: GithubRepoId,
  numbers: Schema.Array(PosInt).check(Schema.isMaxLength(100)),
  headSha: Schema.NullOr(GitSha),
  /** `X-GitHub-Delivery` (dedupe); null for a reconcile. */
  deliveryId: Schema.NullOr(Schema.String.check(Schema.isMaxLength(100))),
  at: Millis,
})
export type GithubPrsDirty = typeof GithubPrsDirty.Type
// #endregion internal

// #region webhooks
const WhId = Schema.Struct({ id: GithubInstallationId })
const WhRepo = Schema.Struct({ id: GithubRepoId, full_name: GithubRepo })

/** `installation` (created, deleted, suspend, unsuspend, new_permissions_accepted). */
export const WhInstallation = Schema.Struct({
  action: Schema.Literals(["created", "deleted", "suspend", "unsuspend", "new_permissions_accepted"]),
  installation: Schema.Struct({
    id: GithubInstallationId,
    account: Schema.Struct({ login: GithubLogin, id: Schema.Int, type: GithubAccountType }),
    repository_selection: GithubRepositorySelection,
    permissions: Schema.Record(Schema.String, Schema.String),
  }),
  /** Who installed (or approved) it: a link proof (§2, one installation, one workspace). */
  sender: Schema.optionalKey(Schema.Struct({ login: GithubLogin, id: Schema.optionalKey(Schema.Int) })),
  /** On `created` after an approval: the person who requested the installation (also a link proof). */
  requester: Schema.optionalKey(
    Schema.NullOr(Schema.Struct({ login: GithubLogin, id: Schema.optionalKey(Schema.Int) })),
  ),
})
export type WhInstallation = typeof WhInstallation.Type

/** `installation_repositories` (added, removed). */
export const WhInstallationRepos = Schema.Struct({
  action: Schema.Literals(["added", "removed"]),
  installation: WhId,
  repository_selection: Schema.optionalKey(GithubRepositorySelection),
  repositories_added: Schema.Array(WhRepo),
  repositories_removed: Schema.Array(WhRepo),
})
export type WhInstallationRepos = typeof WhInstallationRepos.Type

/** `pull_request`, `pull_request_review`, `pull_request_review_comment`, `pull_request_review_thread`. */
export const WhPullRequestish = Schema.Struct({
  action: Schema.String,
  installation: WhId,
  repository: WhRepo,
  pull_request: Schema.Struct({ number: PosInt, head: Schema.Struct({ sha: GitSha }) }),
})
export type WhPullRequestish = typeof WhPullRequestish.Type

/** `issue_comment`: only a comment on a PR (`issue.pull_request` present) rings. Issues never create items. */
export const WhIssueComment = Schema.Struct({
  action: Schema.String,
  installation: WhId,
  repository: WhRepo,
  issue: Schema.Struct({
    number: PosInt,
    pull_request: Schema.optionalKey(Schema.Struct({ url: Schema.String })),
  }),
})
export type WhIssueComment = typeof WhIssueComment.Type

const WhCheckPrs = Schema.Array(Schema.Struct({ number: PosInt }))

/** `check_suite`. `pull_requests` is empty for a fork's PR: resolve by `head_sha`. */
export const WhCheckSuite = Schema.Struct({
  action: Schema.String,
  installation: WhId,
  repository: WhRepo,
  check_suite: Schema.Struct({ head_sha: GitSha, pull_requests: WhCheckPrs }),
})
export type WhCheckSuite = typeof WhCheckSuite.Type

/** `check_run`. Same as `check_suite`. */
export const WhCheckRun = Schema.Struct({
  action: Schema.String,
  installation: WhId,
  repository: WhRepo,
  check_run: Schema.Struct({ head_sha: GitSha, pull_requests: WhCheckPrs }),
})
export type WhCheckRun = typeof WhCheckRun.Type

/** `status` (a commit status): no action, no PR numbers; resolved by `sha`. */
export const WhStatus = Schema.Struct({
  installation: WhId,
  repository: WhRepo,
  sha: GitSha,
})
export type WhStatus = typeof WhStatus.Type

/** `repository`: `renamed` and `transferred` update the registry's remote; `deleted` is lost access. */
export const WhRepository = Schema.Struct({
  action: Schema.String,
  installation: WhId,
  repository: Schema.Struct({ id: GithubRepoId, full_name: GithubRepo, default_branch: Schema.String }),
  changes: Schema.optionalKey(Schema.Unknown),
})
export type WhRepository = typeof WhRepository.Type
// #endregion webhooks
